import AccountService from "../services/AccountService.js";

// Doivent rester alignés sur `passwordSchema` (server/src/routes/auth.js) : le
// formulaire est en `novalidate`, donc rien ne filtre la saisie avant l'envoi.
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 200;

/**
 * Page de réinitialisation : lit `?token=` dans l'URL et permet de définir un
 * nouveau mot de passe via l'API. Atterrissage du magic link reçu par email.
 */
export default class ResetPassword {
    constructor() {
        this.page = document.querySelector(".page.reset-password");
        if (!this.page) return;

        this.t = (k) => window.geoNerdApp.languageManager.t(k);
        this.form = this.page.querySelector("form");
        this.submitBtn = this.form?.querySelector('button[type="submit"]');
        this.message = this.page.querySelector(".account-message");
        this.loginLink = this.page.querySelector(".reset-login-link");
        this.token = new URLSearchParams(window.location.search).get("token");

        this.init();
    }

    init() {
        if (!this.token) {
            this.disableForm();
            this.showError(this.t("account.reset.missingToken"));
            return;
        }
        this.form?.addEventListener("submit", (e) => this.handleSubmit(e));
    }

    async handleSubmit(e) {
        e.preventDefault();
        const password = this.form.querySelector('input[type="password"]').value;

        // Vérifié ici plutôt que de laisser le serveur répondre 400 : son 400
        // « mot de passe refusé » est peu distinguable du 400 « token refusé »,
        // et l'appel consommerait le quota du rate-limit strict de la route.
        if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
            this.showError(this.t("account.errors.invalidPassword"));
            return;
        }

        this.setBusy(true);
        const res = await AccountService.resetPassword(this.token, password);
        this.setBusy(false);

        if (res.ok) {
            this.disableForm();
            this.showSuccess(this.t("account.reset.success"));
            return;
        }
        this.showError(this.errorMessage(res));
    }

    /**
     * Un 400 couvre deux cas sans rapport : le token refusé par le serveur
     * (`{ error: "invalid_token" }`) et le corps rejeté par le schéma Fastify.
     * Les confondre affichait « ce lien est invalide ou a expiré » sur un lien
     * parfaitement valide dont seul le mot de passe posait problème.
     */
    errorMessage(res) {
        if (res.status === 0) return this.t("account.errors.network");
        if (res.status === 429) return this.t("account.errors.tooMany");
        if (res.status === 400) {
            if (res.data?.error === "invalid_token") return this.t("account.reset.invalidToken");
            // Erreur de validation du schéma : le chemin du champ est dans le
            // message (`body/password must NOT have fewer than 8 characters`).
            // Le garde ci-dessus rend ce cas improbable — il reste le filet si
            // la règle serveur évolue sans les constantes de ce fichier.
            if (typeof res.data?.message === "string" && res.data.message.includes("password")) {
                return this.t("account.errors.invalidPassword");
            }
            // Token absent ou tronqué par le client mail : le lien est bien cassé.
            return this.t("account.reset.invalidToken");
        }
        return this.t("account.errors.generic");
    }

    disableForm() {
        this.form?.querySelectorAll("input, button").forEach((el) => (el.disabled = true));
    }

    setBusy(busy) {
        const btn = this.form?.querySelector('button[type="submit"]');
        if (btn) btn.disabled = busy;
    }

    showError(msg) {
        if (this.message) {
            this.message.textContent = msg;
            this.message.className = "account-message error";
        }
    }

    showSuccess(msg) {
        if (this.message) {
            this.message.textContent = msg;
            this.message.className = "account-message success";
        }
        this.submitBtn?.classList.add("hidden");
        if (this.loginLink) {
            this.loginLink.classList.remove("hidden");
            this.loginLink.addEventListener("click", (e) => {
                e.preventDefault();
                // La connexion a sa propre page depuis la refonte du menu : plus de
                // popup à ouvrir, on y navigue.
                const lang = window.geoNerdApp.languageManager.currentLang;
                window.location.href = lang === "en" ? "/en/login" : "/connexion";
            });
        }
    }
}
